import type { DensityTokens, Theme } from "./types";

export const comfortableDensity: DensityTokens = {
  paddingX: { sm: "0.75rem", md: "1rem", lg: "1.5rem" },
  paddingY: { sm: "0.375rem", md: "0.5rem", lg: "0.75rem" },
  containerPaddingX: "1.5rem", // 24px — matches Card px-6 actual
  containerPaddingY: "1rem", // 16px — matches Card pt-4/py-4 actual
  gap: { sm: "0.5rem", md: "1rem", lg: "1.5rem" },
  fontXs: "0.75rem",
  fontSm: "0.875rem",
  fontBase: "1rem",
  fontLg: "1.125rem",
  fontXl: "1.25rem",
  controlHeight: { sm: "2rem", md: "2.5rem", lg: "3rem" },
  // Checkbox/Radio indicator: matches w-4/w-5/w-6 actual (16/20/24px).
  controlIndicator: { sm: "1rem", md: "1.25rem", lg: "1.5rem" },
  inputRadius: "0.5rem", // rounded-lg actual
  dataTableRow: "3rem",
  dataTableHeader: "3rem",
  cardGap: "1rem",
};

export const compactDensity: DensityTokens = {
  paddingX: { sm: "0.5rem", md: "0.75rem", lg: "1rem" },
  paddingY: { sm: "0.25rem", md: "0.375rem", lg: "0.5rem" },
  containerPaddingX: "1rem", // 16px — matches Card compact px-4 actual
  containerPaddingY: "0.5rem", // 8px — matches Card compact pt-2/pb-2 actual
  gap: { sm: "0.375rem", md: "0.75rem", lg: "1rem" },
  fontXs: "0.6875rem",
  fontSm: "0.8125rem",
  fontBase: "0.875rem",
  fontLg: "1rem",
  fontXl: "1.125rem",
  controlHeight: { sm: "1.75rem", md: "2.125rem", lg: "2.5rem" },
  // Indicators un poco más chicos que comfortable: 14/18/22px.
  controlIndicator: { sm: "0.875rem", md: "1.125rem", lg: "1.375rem" },
  inputRadius: "0.375rem", // rounded-md
  dataTableRow: "2.25rem",
  dataTableHeader: "2.25rem",
  cardGap: "0.75rem",
};

export const denseDensity: DensityTokens = {
  paddingX: { sm: "0.375rem", md: "0.5rem", lg: "0.75rem" },
  paddingY: { sm: "0.125rem", md: "0.25rem", lg: "0.375rem" },
  containerPaddingX: "0.75rem", // 12px
  containerPaddingY: "0.375rem", // 6px
  gap: { sm: "0.25rem", md: "0.5rem", lg: "0.75rem" },
  fontXs: "0.625rem",
  fontSm: "0.75rem",
  fontBase: "0.8125rem",
  fontLg: "0.9375rem",
  fontXl: "1rem",
  controlHeight: { sm: "1.5rem", md: "1.75rem", lg: "2.125rem" },
  // Indicators bien chicos en dense: 12/16/20px.
  controlIndicator: { sm: "0.75rem", md: "1rem", lg: "1.25rem" },
  inputRadius: "0.25rem", // rounded
  dataTableRow: "1.75rem",
  dataTableHeader: "1.75rem",
  cardGap: "0.5rem",
};

export const densityPresets: Record<"comfortable" | "compact" | "dense", DensityTokens> = {
  comfortable: comfortableDensity,
  compact: compactDensity,
  dense: denseDensity,
};

export const lightTheme: Theme = {
  name: "light",
  colors: {
    primary: "#007aff",
    primaryContrast: "#ffffff",
    primaryDark: "#0064d1",
    primaryLight: "#4da6ff",
    secondary: "#6b7280",
    secondaryContrast: "#ffffff",
    secondaryDark: "#4b5563",
    secondaryLight: "#9ca3af",
    success: "#10b981",
    successContrast: "#ffffff",
    successDark: "#059669",
    successLight: "#34d399",
    warning: "#f59e0b",
    warningContrast: "#ffffff",
    warningDark: "#d97706",
    warningLight: "#fbbf24",
    danger: "#ef4444",
    dangerContrast: "#ffffff",
    dangerDark: "#dc2626",
    dangerLight: "#f87171",
    info: "#06b6d4",
    infoContrast: "#ffffff",
    infoDark: "#0891b2",
    infoLight: "#22d3ee",
    accent: "#7c3aed",
    accentContrast: "#ffffff",
    accentDark: "#6d28d9",
    accentLight: "#a78bfa",
    gray50: "#f9fafb",
    gray100: "#f3f4f6",
    gray200: "#e5e7eb",
    gray300: "#d1d5db",
    gray400: "#9ca3af",
    gray500: "#6b7280",
    gray600: "#4b5563",
    gray700: "#374151",
    gray800: "#1f2937",
    gray900: "#111827",
    borderDefault: "#e5e7eb",
    borderFocus: "#007aff",
    borderError: "#ef4444",
    bgDefault: "#ffffff",
    bgSecondary: "#f9fafb",
    bgDisabled: "#f3f4f6",
    textPrimary: "#111827",
    textSecondary: "#374151",
    textMuted: "#6b7280",
    textDisabled: "#9ca3af",
  },
  shadows: {
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  },
  radius: {
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    full: "9999px",
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
  },
  fonts: {
    default: '"Instrument Sans", sans-serif',
    sizeDefault: "16px",
    colorDefault: "#111827",
  },
  density: "comfortable",
  densityTokens: comfortableDensity,
};

export const darkTheme: Theme = {
  name: "dark",
  colors: {
    primary: "#3b82f6",
    primaryContrast: "#ffffff",
    primaryDark: "#1d4ed8",
    primaryLight: "#60a5fa",
    secondary: "#9ca3af",
    secondaryContrast: "#ffffff",
    secondaryDark: "#6b7280",
    secondaryLight: "#d1d5db",
    success: "#10b981",
    successContrast: "#ffffff",
    successDark: "#059669",
    successLight: "#34d399",
    warning: "#f59e0b",
    warningContrast: "#ffffff",
    warningDark: "#d97706",
    warningLight: "#fbbf24",
    danger: "#ef4444",
    dangerContrast: "#ffffff",
    dangerDark: "#dc2626",
    dangerLight: "#f87171",
    info: "#06b6d4",
    infoContrast: "#ffffff",
    infoDark: "#0891b2",
    infoLight: "#22d3ee",
    accent: "#a78bfa",
    accentContrast: "#ffffff",
    accentDark: "#7c3aed",
    accentLight: "#c4b5fd",
    gray50: "#111827",
    gray100: "#1f2937",
    gray200: "#374151",
    gray300: "#4b5563",
    gray400: "#6b7280",
    gray500: "#9ca3af",
    gray600: "#d1d5db",
    gray700: "#e5e7eb",
    gray800: "#f3f4f6",
    gray900: "#f9fafb",
    borderDefault: "#374151",
    borderFocus: "#3b82f6",
    borderError: "#ef4444",
    bgDefault: "#111827",
    bgSecondary: "#1f2937",
    bgDisabled: "#374151",
    textPrimary: "#f9fafb",
    textSecondary: "#e5e7eb",
    textMuted: "#9ca3af",
    textDisabled: "#6b7280",
  },
  shadows: {
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.3)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.4), 0 2px 4px -2px rgb(0 0 0 / 0.4)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.4), 0 4px 6px -4px rgb(0 0 0 / 0.4)",
  },
  radius: {
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    full: "9999px",
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
  },
  fonts: {
    default: '"Instrument Sans", sans-serif',
    sizeDefault: "16px",
    colorDefault: "#f9fafb",
  },
  density: "comfortable",
  densityTokens: comfortableDensity,
};

export const blueTheme: Theme = {
  name: "blue",
  colors: {
    primary: "#1e40af",
    primaryContrast: "#ffffff",
    primaryDark: "#1e3a8a",
    primaryLight: "#3b82f6",
    secondary: "#64748b",
    secondaryContrast: "#ffffff",
    secondaryDark: "#475569",
    secondaryLight: "#94a3b8",
    success: "#059669",
    successContrast: "#ffffff",
    successDark: "#047857",
    successLight: "#10b981",
    warning: "#d97706",
    warningContrast: "#ffffff",
    warningDark: "#b45309",
    warningLight: "#f59e0b",
    danger: "#dc2626",
    dangerContrast: "#ffffff",
    dangerDark: "#b91c1c",
    dangerLight: "#ef4444",
    info: "#0891b2",
    infoContrast: "#ffffff",
    infoDark: "#0e7490",
    infoLight: "#06b6d4",
    accent: "#f43f5e",
    accentContrast: "#ffffff",
    accentDark: "#e11d48",
    accentLight: "#fb7185",
    gray50: "#f8fafc",
    gray100: "#f1f5f9",
    gray200: "#e2e8f0",
    gray300: "#cbd5e1",
    gray400: "#94a3b8",
    gray500: "#64748b",
    gray600: "#475569",
    gray700: "#334155",
    gray800: "#1e293b",
    gray900: "#0f172a",
    borderDefault: "#e2e8f0",
    borderFocus: "#1e40af",
    borderError: "#dc2626",
    bgDefault: "#ffffff",
    bgSecondary: "#f8fafc",
    bgDisabled: "#f1f5f9",
    textPrimary: "#0f172a",
    textSecondary: "#334155",
    textMuted: "#64748b",
    textDisabled: "#94a3b8",
  },
  shadows: {
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  },
  radius: {
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    full: "9999px",
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
  },
  fonts: {
    default: '"Instrument Sans", sans-serif',
    sizeDefault: "16px",
    colorDefault: "#0f172a",
  },
  density: "comfortable",
  densityTokens: comfortableDensity,
};

export const greenTheme: Theme = {
  name: "green",
  colors: {
    primary: "#059669",
    primaryContrast: "#ffffff",
    primaryDark: "#047857",
    primaryLight: "#10b981",
    secondary: "#6b7280",
    secondaryContrast: "#ffffff",
    secondaryDark: "#4b5563",
    secondaryLight: "#9ca3af",
    success: "#16a34a",
    successContrast: "#ffffff",
    successDark: "#15803d",
    successLight: "#22c55e",
    warning: "#d97706",
    warningContrast: "#ffffff",
    warningDark: "#b45309",
    warningLight: "#f59e0b",
    danger: "#dc2626",
    dangerContrast: "#ffffff",
    dangerDark: "#b91c1c",
    dangerLight: "#ef4444",
    info: "#0891b2",
    infoContrast: "#ffffff",
    infoDark: "#0e7490",
    infoLight: "#06b6d4",
    accent: "#6366f1",
    accentContrast: "#ffffff",
    accentDark: "#4f46e5",
    accentLight: "#818cf8",
    gray50: "#f9fafb",
    gray100: "#f3f4f6",
    gray200: "#e5e7eb",
    gray300: "#d1d5db",
    gray400: "#9ca3af",
    gray500: "#6b7280",
    gray600: "#4b5563",
    gray700: "#374151",
    gray800: "#1f2937",
    gray900: "#111827",
    borderDefault: "#e5e7eb",
    borderFocus: "#059669",
    borderError: "#dc2626",
    bgDefault: "#ffffff",
    bgSecondary: "#f9fafb",
    bgDisabled: "#f3f4f6",
    textPrimary: "#111827",
    textSecondary: "#374151",
    textMuted: "#6b7280",
    textDisabled: "#9ca3af",
  },
  shadows: {
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  },
  radius: {
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    full: "9999px",
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
  },
  fonts: {
    default: '"Instrument Sans", sans-serif',
    sizeDefault: "16px",
    colorDefault: "#111827",
  },
  density: "comfortable",
  densityTokens: comfortableDensity,
};

export const defaultTheme = lightTheme;

export const themes: Record<string, Theme> = {
  light: lightTheme,
  dark: darkTheme,
  blue: blueTheme,
  green: greenTheme,
};
